import { type ObjectId } from 'mongodb';
import { MaybeDocId, ToolOutputs } from '~api/types';
import { omit, Vals } from '~lib';

export namespace Artifact {
  export const formats = {
    Csv: 'csv',
    Pdf: 'pdf',
    Png: 'png',
    Json: 'json',
    Html: 'html',
    Markdown: 'markdown',
    Xlsx: 'xlsx',
  } as const;

  export type Format = Vals<typeof formats>;
  export const isAcceptedFormat = (f: string): f is Format =>
    Object.values(formats).includes(f as Format);

  /**
   * @desc Formats a browser renders as a picture, so `![alt](artifact:<id>)` is a legitimate
   * inline embed for them and a link (`[label](artifact:<id>)`) for everything else. Shared
   * because both ends of that contract have to agree: the backend rejects an image embed
   * pointing at a non-image artefact, and the portal falls back to a link card if one arrives.
   */
  export const imageFormats: readonly Format[] = [formats.Png];
  export const isImageFormat = (f: string): f is Format => imageFormats.includes(f as Format);

  export type Model<
    OId extends string | ObjectId = ObjectId,
    TDate extends string | Date = Date,
    TDocId extends void | string | ObjectId = OId,
  > = MaybeDocId<TDocId> & {
    refs: {
      userId: string;
      conversationId?: OId;
      toolUseId?: string;
      /** @desc The turn that produced this artefact, for tracing a deliverable back to its prompt. */
      turnId?: string;
    };
    format: Format;
    schema?: ToolOutputs.Schema;
    location: {
      bucket: string;
      file: string;
    };
    /**
     * @desc Display properties, not identity: the basename the producer chose (no extension,
     * e.g. `annual-ggr-chart`) and the one-line description it declared. Both optional because
     * documents written before H2-219 have neither; the write path always sets them.
     */
    name?: string;
    description?: string;
    /** @desc sha256 hex of the stored bytes — the only thing procedural dedupe compares. */
    contentHash?: string;
    /** @desc Stored bytes. */
    size?: number;
    /**
     * @desc Lineage: a deliverable is a chain of artefacts sharing a format, identified by
     * `lineageId` (the `_id` of revision 1). A revision is DECLARED by the producer, never
     * inferred from a name collision. `revision` is 1-based; `supersedes` names the previous
     * head and is absent on revision 1.
     */
    revision?: number;
    lineageId?: OId;
    supersedes?: OId;
    /**
     * @desc Provenance, declared by the producer: the artefact these bytes were built from (an
     * xlsx from a csv, a chart from a dataset). Any format, and tracking only — it never takes
     * part in dedupe, in the latest-revision fold, or in supersession.
     */
    derivedFrom?: OId;
    createdAt: TDate;
    updatedAt: TDate;
  };

  export const toSerial = (a: Model): Model<string, string> => {
    // lineageId/supersedes are destructured out rather than overridden in place: a conditional
    // spread would leave the ObjectId from `...a` standing whenever the key is absent, which the
    // serial type has no way to express.
    const { lineageId, supersedes, derivedFrom, ...rest } = a;
    return {
      ...rest,
      _id: a._id.toString(),
      refs: {
        ...a.refs,
        conversationId: a.refs.conversationId?.toString(),
      },
      ...(lineageId ? { lineageId: lineageId.toString() } : {}),
      ...(supersedes ? { supersedes: supersedes.toString() } : {}),
      ...(derivedFrom ? { derivedFrom: derivedFrom.toString() } : {}),
      createdAt: a.createdAt.toISOString(),
      updatedAt: a.updatedAt.toISOString(),
    };
  };

  /**
   * @desc A signed, expiring download link for one artefact. `name` is the producer's basename
   * (ADR-21), absent on artefacts stored before names existed; `format` travels with the URL
   * because an inline `artifact:<id>` reference carries only an id.
   */
  export type SignedUrl = { url: string; expiresAt: Date; format: Format; name?: string };

  /**
   * @desc Artefact counts: `total` plus one entry per format present. Formats with no artefacts
   * are absent from `byFormat` rather than zero, so a client iterates only what exists.
   */
  export type FormatCounts = { total: number; byFormat: Partial<Record<Format, number>> };

  export const emptyFormatCounts = (): FormatCounts => ({ total: 0, byFormat: {} });

  /**
   * @desc Admin inspector projection of one artefact: display and lineage metadata, never the
   * storage location (downloads go through `admin.getArtifactDownload`). `turnId`/`toolUseId`
   * are lifted out of `refs` so a row joins straight onto the turn waterfall.
   */
  export const toInspectorItem = (a: Model) => ({
    _id: a._id.toString(),
    ownerUserId: a.refs.userId,
    conversationId: a.refs.conversationId?.toString() ?? null,
    turnId: a.refs.turnId ?? null,
    toolUseId: a.refs.toolUseId ?? null,
    name: a.name ?? null,
    description: a.description ?? null,
    format: a.format,
    size: a.size ?? null,
    revision: a.revision ?? null,
    lineageId: a.lineageId?.toString() ?? null,
    supersedes: a.supersedes?.toString() ?? null,
    derivedFrom: a.derivedFrom?.toString() ?? null,
    createdAt: a.createdAt,
  });
  export type InspectorItem = ReturnType<typeof toInspectorItem>;

  // prepare with signed download link accessible from the UI
  export const toPublic = (a: Model, url: string) => ({
    ...omit(toSerial(a), 'location'),
    url,
  });
  export type Public = ReturnType<typeof toPublic>;
}
